import { useEffect, useSyncExternalStore } from 'react';
import {
  Node,
  ReactNodeViewRenderer,
  NodeViewWrapper,
  type ReactNodeViewProps,
} from '@tiptap/react';
import { useNavigateToEntity } from '@/hooks/navigation';
import { useWorkspaceDocs } from '@/lib/sync/hooks/use-workspace-docs';
import { useWorkspaceService } from '@/lib/workspace/use-workspace-service';
import { CanvasEmbedCard } from './canvas-embed-card';
import { resolveCanvasEmbedState } from './canvas-embed-state';
import {
  isCanvasPending,
  clearCanvasPending,
  subscribeCanvasPending,
} from './canvas-pending-store';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    canvasEmbed: {
      /** Insert a canvas embed referencing the canvas entity `canvasId`. */
      insertCanvasEmbed: (canvasId: string) => ReturnType;
    };
  }
}

/** React node view — renders the embed card and wires open / restore / remove. */
function CanvasEmbedNodeView({ node, getPos, editor }: ReactNodeViewProps) {
  const canvasId = (node.attrs.canvasId as string) ?? '';
  const navigateToEntity = useNavigateToEntity();
  const service = useWorkspaceService();
  const docs = useWorkspaceDocs();

  const canvas = docs.find((d) => d.id === canvasId);
  const pending = useSyncExternalStore(subscribeCanvasPending, () => isCanvasPending(canvasId));
  const state = resolveCanvasEmbedState(canvas, pending);

  // Once the canvas row syncs in, the generation is resolved — drop the pending flag.
  useEffect(() => {
    if (canvas && pending) clearCanvasPending(canvasId);
  }, [canvas, pending, canvasId]);

  const removeNode = () => {
    if (typeof getPos !== 'function') return;
    const pos = getPos();
    if (pos == null) return;
    editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
  };

  return (
    <NodeViewWrapper data-canvas-embed-view>
      <CanvasEmbedCard
        canvasId={canvasId}
        title={state === 'pending' ? undefined : canvas?.title || 'Untitled'}
        state={state}
        onOpen={() => {
          if (canvasId) navigateToEntity(canvasId);
        }}
        onRestore={() => {
          if (!canvasId) return;
          service.archiveDoc(canvasId, false);
          navigateToEntity(canvasId);
        }}
        onRemove={removeNode}
      />
    </NodeViewWrapper>
  );
}

/**
 * Canvas embed — a block-level atom node carrying just the `canvasId` of a
 * canvas entity. The Yjs doc commits only to "there's an embed for this canvas
 * here"; the canvas's own content lives in its entity room, rendered live by
 * the node view on demand.
 *
 * Serializes to `<div data-canvas-embed data-canvas-id="…">` so it round-trips
 * through the Yjs doc / HTML clipboard. Inserted by the `/canvas` slash command
 * via the `insertCanvasEmbed` command.
 */
export const CanvasExtension = Node.create({
  name: 'canvasEmbed',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      canvasId: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-canvas-id') ?? '',
        renderHTML: (attrs) => ({ 'data-canvas-id': attrs.canvasId as string }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-canvas-embed]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', { ...HTMLAttributes, 'data-canvas-embed': '' }];
  },

  addNodeView() {
    return ReactNodeViewRenderer(CanvasEmbedNodeView);
  },

  addCommands() {
    return {
      insertCanvasEmbed:
        (canvasId: string) =>
        ({ commands }: { commands: import('@tiptap/core').SingleCommands }) => {
          const trimmed = canvasId?.trim();
          if (!trimmed) return false;
          return commands.insertContent({ type: this.name, attrs: { canvasId: trimmed } });
        },
    };
  },
});
